import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Music, Headphones, ArrowRight, ExternalLink, ArrowLeft, ShieldCheck } from 'lucide-react';
import { api } from '../app/api';
import { logger } from '../lib/logger';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { buildSpotifyEmbedUrl, PLAYLIST_CATEGORIES, categoryMeta } from '../lib/spotify';
import { useDocumentHead } from './blog/useDocumentHead';

const SITE_BASE = 'https://apexathleticmind.com';

function PlaylistCard({ playlist }) {
  const embedUrl = buildSpotifyEmbedUrl(playlist);
  const cat = categoryMeta(playlist.category);

  return (
    <article
      className="group relative rounded-2xl border border-white/10 bg-[#0F1314] overflow-hidden shadow-xl shadow-black/30 hover:ring-1 hover:ring-[#4FBCC7]/40 transition-shadow"
      data-testid={`playlist-card-${playlist.id}`}
    >
      <div className="px-5 pt-5 pb-4">
        <div className="flex items-center justify-between gap-3 mb-3">
          <span className="font-mono-accent uppercase tracking-[0.2em] text-[10px] text-[#4FBCC7] font-semibold">
            {cat.label}
          </span>
          <a
            href={playlist.spotify_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-[11px] text-white/60 hover:text-white transition-colors"
            data-testid={`playlist-open-spotify-${playlist.id}`}
          >
            Open on Spotify
            <ExternalLink className="h-3 w-3" />
          </a>
        </div>
        <h3 className="font-heading text-[1.2rem] font-bold tracking-tight text-white leading-tight mb-1">
          {playlist.title}
        </h3>
        {playlist.description && (
          <p className="text-[13px] text-white/60 leading-snug">{playlist.description}</p>
        )}
      </div>
      <div className="px-3 pb-3">
        {embedUrl ? (
          <iframe
            title={`Spotify player: ${playlist.title}`}
            src={embedUrl}
            width="100%"
            height="352"
            frameBorder="0"
            allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
            loading="lazy"
            className="rounded-xl bg-black/40"
            data-testid={`playlist-iframe-${playlist.id}`}
          />
        ) : (
          <div className="rounded-xl bg-white/5 p-5 text-center text-[12.5px] text-white/65">
            Embed unavailable.
          </div>
        )}
      </div>
    </article>
  );
}

export default function Playlists() {
  const [playlists, setPlaylists] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
    let active = true;
    api.get('/api/playlists')
      .then((data) => { if (active) setPlaylists(data?.playlists || []); })
      .catch((err) => {
        logger.error('Failed to load playlists', err);
        if (active) setError(err.message || 'Could not load playlists');
      });
    return () => { active = false; };
  }, []);

  useDocumentHead({
    title: 'The Locker Room — Pre-Game Playlists | Apex Athletic Mind',
    description: 'Curated Spotify playlists for athletes: pre-game lock-in, in-the-zone flow, and recovery resets. Train your mind. Elevate your game.',
    canonical: `${SITE_BASE}/playlists`,
    ogType: 'website',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: 'The Locker Room — Pre-Game Playlists',
      url: `${SITE_BASE}/playlists`,
      description: 'Curated Spotify playlists for athletes from Apex Athletic Mind.',
    },
  });

  const grouped = useMemo(() => {
    const map = {};
    (playlists || []).forEach((p) => {
      const key = p.category || 'pregame';
      if (!map[key]) map[key] = [];
      map[key].push(p);
    });
    return map;
  }, [playlists]);

  const categoriesWithPlaylists = PLAYLIST_CATEGORIES.filter((c) => grouped[c.id]?.length);

  return (
    <main
      className="min-h-screen bg-[#0B0C0D] text-white"
      data-testid="playlists-page"
    >
      {/* Top bar */}
      <header className="sticky top-0 z-30 border-b border-white/8 bg-[#0B0C0D]/80 backdrop-blur supports-[backdrop-filter]:bg-[#0B0C0D]/65">
        <div className="max-w-[1120px] mx-auto px-5 sm:px-8 py-4 flex items-center justify-between">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-[13px] font-medium text-white/70 hover:text-white transition-colors"
            data-testid="playlists-back-home"
          >
            <ArrowLeft className="h-4 w-4" />
            Apex Athletic Mind
          </Link>
          <span className="inline-flex items-center gap-2 font-mono-accent text-[10px] uppercase tracking-[0.22em] text-[#4FBCC7] font-semibold">
            <Headphones className="h-3.5 w-3.5" />
            The Locker Room
          </span>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-[1120px] mx-auto px-5 sm:px-8 pt-14 sm:pt-20 pb-10">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mb-4">
          <div className="inline-flex items-center gap-2 font-mono-accent uppercase tracking-[0.22em] text-[10px] text-[#4FBCC7] font-semibold">
            <Music className="h-3.5 w-3.5" />
            Pre-Game Playlists
          </div>
          <span
            className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-emerald-500/10 ring-1 ring-emerald-400/30 font-mono-accent uppercase tracking-[0.18em] text-[9.5px] text-emerald-200 font-semibold"
            data-testid="playlists-clean-badge"
            title="Every track is verified clean / radio-edit by Coach Lewis."
          >
            <ShieldCheck className="h-3 w-3" strokeWidth={2.5} />
            Clean lyrics &middot; Coach Lewis curated
          </span>
        </div>
        <h1
          className="font-heading text-[2.4rem] sm:text-[3.4rem] font-bold tracking-tight leading-[1.04] text-white mb-5 max-w-[22ch]"
          data-testid="playlists-title"
        >
          Press play.<br />Lock in.
        </h1>
        <p className="text-[16.5px] text-white/65 leading-[1.7] max-w-[58ch]">
          Curated by Coach Lewis to match the mental performance work. <strong className="text-white">Every track is clean / radio-edit</strong> &mdash; appropriate for middle-school and high-school athletes. Spotify Free or Premium &mdash; both work.
        </p>
      </section>

      <div className="max-w-[1120px] mx-auto px-5 sm:px-8 pb-20">
        {error && (
          <div className="rounded-xl border border-red-400/30 bg-red-500/10 p-5 text-[14px] text-red-200" role="alert" data-testid="playlists-error">
            {error}
          </div>
        )}

        {!playlists && !error && (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="playlists-loading">
            {[0, 1, 2].map((i) => (
              <Skeleton key={`sk-${i}`} className="h-[420px] rounded-2xl bg-white/5" />
            ))}
          </div>
        )}

        {playlists && playlists.length === 0 && (
          <div className="rounded-3xl border border-white/10 bg-white/5 p-12 text-center" data-testid="playlists-empty">
            <Music className="h-10 w-10 mx-auto text-white/30 mb-4" />
            <h2 className="font-heading text-[1.4rem] font-semibold mb-2">Curating clean playlists &mdash; back soon.</h2>
            <p className="text-white/55 max-w-[48ch] mx-auto">
              Coach Lewis is rebuilding the playlist catalog with clean / radio-edit tracks only. Drop back in &mdash; or train your mind with the assessment in the meantime.
            </p>
            <Button asChild className="mt-5 bg-white text-[#0B0C0D] hover:bg-white/90 h-11 px-5 rounded-xl">
              <Link to="/assessment">Take the $27 Self-Assessment</Link>
            </Button>
          </div>
        )}

        {playlists && playlists.length > 0 && (
          <div className="space-y-14">
            {categoriesWithPlaylists.map((cat) => (
              <section key={cat.id} data-testid={`playlist-section-${cat.id}`}>
                <div className="flex items-end justify-between gap-4 mb-5">
                  <div>
                    <div className="font-mono-accent uppercase tracking-[0.22em] text-[10px] text-[#4FBCC7] font-semibold mb-1.5">
                      Category
                    </div>
                    <h2 className="font-heading text-[1.5rem] sm:text-[1.75rem] font-bold tracking-tight text-white">
                      {cat.label}
                    </h2>
                    <p className="text-[13.5px] text-white/55 mt-1">{cat.tagline}</p>
                  </div>
                </div>
                <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                  {grouped[cat.id].map((pl) => (
                    <PlaylistCard key={pl.id} playlist={pl} />
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}

        {/* Conversion strip */}
        <section
          className="mt-20 rounded-3xl bg-gradient-to-br from-[#004C54] via-[#003a40] to-[#0B0C0D] p-8 sm:p-12 ring-1 ring-white/10 shadow-2xl shadow-black/30"
          data-testid="playlists-cta"
        >
          <div className="font-mono-accent uppercase tracking-[0.22em] text-[10px] text-[#4FBCC7] font-semibold mb-3">
            The Apex Method
          </div>
          <h2 className="font-heading text-[1.75rem] sm:text-[2.1rem] font-bold tracking-tight leading-[1.1] mb-4 max-w-[24ch]">
            Music is the warm-up. Mental performance is the work.
          </h2>
          <p className="text-[15px] text-white/70 max-w-[55ch] leading-[1.65] mb-6">
            Pair the playlist with the protocol. The Apex Six Self-Assessment maps your pressure profile in 8 minutes and returns a personalized Individual Development Plan.
          </p>
          <div className="flex flex-wrap gap-3">
            <Button asChild className="bg-white text-[#0B0C0D] hover:bg-white/90 h-11 px-5 rounded-xl font-semibold" data-testid="playlists-cta-assessment">
              <Link to="/assessment">
                Take the $27 Self-Assessment
                <ArrowRight className="h-4 w-4 ml-1" />
              </Link>
            </Button>
            <Button asChild variant="outline" className="h-11 px-5 rounded-xl border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white" data-testid="playlists-cta-quiz">
              <Link to="/quiz">Try the Free Mindset Quiz</Link>
            </Button>
          </div>
        </section>
      </div>
    </main>
  );
}
